import React, { useState } from 'react'
import { Table } from 'antd'
import { useTranslation } from 'react-i18next'
import { moment } from '@BUP/configs/i18n'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import ModalComponent from '@BUP/components/Generics/Modal'

/**
 * The counselor's course-assignment period history: every period of the current term, open or
 * closed, with its completion count - and the only place a closed period can be reopened. Before
 * this, closing was one-way and the only way forward was opening a new, empty period, which made
 * every student who had already answered start over.
 *
 * Only one period per grade can be open at a time (backend constraint), so "Reabrir" is disabled
 * on a closed period while its grade has another period open, with a hint saying which one to close
 * first - and open periods can be closed right here, so swapping which period of a grade is open
 * (the usual fix after closing the wrong one) doesn't need a trip back to the dashboard.
 *
 * Closed periods also offer the course-generator input files (see useDownloadCourseGeneratorFiles):
 * once a period is closed its answers are final, which is when they get handed to the Python
 * course builder. `downloadingPeriodId` disables only that row's button while it downloads.
 */
const PeriodsTable = ({
  isLoading,
  error,
  periods,
  onReopen,
  isReopening,
  onClose,
  isClosing,
  onViewResponses,
  onDownloadFiles,
  downloadingPeriodId
}) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  // { action: 'reopen' | 'close', period } while its confirmation dialog is showing.
  const [pendingConfirmation, setPendingConfirmation] = useState(null)

  if (isLoading) return <BupSpinner size='large' />

  if (error) {
    return (
      <AlertComponent
        type='error'
        messageKey='common.resources.course-assignment.actions.read.failure'
        error={error}
      />
    )
  }

  if (!periods.length) {
    return <p>{t('historyEmpty')}</p>
  }

  const openPeriodByGrade = new Map(
    periods.filter(period => period.status === 'open').map(period => [period.grade, period])
  )

  const handleConfirm = async () => {
    const { action, period } = pendingConfirmation
    try {
      if (action === 'reopen') await onReopen(period.id)
      if (action === 'close') await onClose(period.id)
    } finally {
      setPendingConfirmation(null)
    }
  }

  const renderReopen = (period) => {
    const blockingPeriod = openPeriodByGrade.get(period.grade)
    return (
      <>
        <ButtonComponent
          buttonText={t('historyReopenButton')}
          colorType='primary'
          size='small'
          onClick={() => setPendingConfirmation({ action: 'reopen', period })}
          disabled={isReopening || !!blockingPeriod}
        />
        {blockingPeriod && (
          <span>
            {t('historyReopenBlocked', {
              date: blockingPeriod.opensAt ? moment(blockingPeriod.opensAt).format('lll') : ''
            })}
          </span>
        )}
      </>
    )
  }

  const columns = [
    {
      title: t('historyColumnGrade'),
      dataIndex: 'grade',
      key: 'grade'
    },
    {
      title: t('historyColumnStatus'),
      key: 'status',
      render: (_, period) => period.status === 'open' ? t('statusOpen') : t('statusClosed')
    },
    {
      title: t('historyColumnOpenedAt'),
      key: 'opensAt',
      render: (_, period) => period.opensAt ? moment(period.opensAt).format('lll') : ''
    },
    {
      title: t('historyColumnClosedAt'),
      key: 'closedAt',
      render: (_, period) => {
        if (!period.closedAt) return ''
        const date = moment(period.closedAt).format('lll')
        return period.closedByName ? t('historyClosedBy', { date, name: period.closedByName }) : date
      }
    },
    {
      title: t('historyColumnResponses'),
      key: 'responses',
      render: (_, period) => t('completionCount', { completed: period.completedCount ?? 0, eligible: period.eligibleCount ?? 0 })
    },
    {
      title: t('historyColumnAllowOtherCourses'),
      key: 'allowOtherCourses',
      render: (_, period) => period.allowOtherCourses ? t('historyYes') : t('historyNo')
    },
    {
      title: t('historyColumnActions'),
      key: 'actions',
      render: (_, period) => (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonComponent
            buttonText={t('viewResponsesButton')}
            colorType='tertiary'
            size='small'
            onClick={() => onViewResponses(period)}
          />
          {period.status === 'closed' && (
            <ButtonComponent
              buttonText={t('downloadFilesButton')}
              colorType='tertiary'
              size='small'
              onClick={() => onDownloadFiles(period)}
              disabled={downloadingPeriodId != null}
              loading={String(downloadingPeriodId) === String(period.id)}
            />
          )}
          {period.status === 'closed' && renderReopen(period)}
          {period.status === 'open' && (
            <ButtonComponent
              buttonText={t('historyCloseButton')}
              colorType='secondary'
              size='small'
              onClick={() => setPendingConfirmation({ action: 'close', period })}
              disabled={isClosing}
            />
          )}
        </div>
      )
    }
  ]

  return (
    <>
      <Table
        columns={columns}
        dataSource={periods}
        rowKey='id'
        pagination={false}
        scroll={{ x: true }}
      />
      <ModalComponent
        stateModal={!!pendingConfirmation}
        onClickCloseModal={() => setPendingConfirmation(null)}
        modalTitle={pendingConfirmation?.action === 'close'
          ? t('closeConfirmTitle')
          : t('reopenConfirmTitle', { grade: pendingConfirmation?.period.grade ?? '' })}
        modalDescription={
          <p>
            {pendingConfirmation?.action === 'close'
              ? t('closeConfirmDescription', { count: pendingConfirmation.period.completedCount ?? 0 })
              : t('reopenConfirmDescription', { count: pendingConfirmation?.period.completedCount ?? 0 })}
          </p>
        }
        confirmButtonText={pendingConfirmation?.action === 'close' ? t('closeConfirmButton') : t('reopenConfirmButton')}
        loading={isReopening || isClosing}
        onClickConfirm={handleConfirm}
        dataTest='course-assignment-period-history-confirm'
      />
    </>
  )
}

export default PeriodsTable
